import {
  DBAppointments,
  DBBillingAddress,
  DBClientSupplierOrderItem,
  DBCustomerOrderItem,
  DBDeliveryAddress,
  DBDeliveryTypes,
  DBOrderDocument,
  DBPaymentTypes,
  DBTransferOrderItem,
  DBWorkOrders,
  DeliveryType,
  WorkOrdersStatus,
} from '@prisma/client';
import {
  CreateServiceReservationReq,
  DBCustomerOrderItemResponse,
  DBCustomerOrderStatusEnum,
  DBOrderItems,
  InProcessSubStatuses,
  OrderStatusProps,
} from './customer-order-item';
import { InboundInvoiceItem } from './inbound-invoice';

export interface ISourceWarehouseDetails {
  branchId: number | null | undefined;
  erpId: string;
  quantity: number;
  ean: string;
  isSupplierStock: boolean;
}

export interface IReservedOnLocation {
  stockLocationId: string;
  qrCode: string;
  reserved: number;
}
export interface IWorkOrderLineItem {
  erpId: string;
  ean: string;
  productName: string;
  brand: string;
  quantity: number;
  received: number;
  deliveryDateFrom?: string;
  deliveryDateTo?: string;
  sku: string;
  sellingPrice: number;
  cashDiscount?: number;
  reservedOnLocation?: IReservedOnLocation[];
  dot: string;
  branchId?: number | null | undefined;
  sourceWarehouseDetails?: ISourceWarehouseDetails[];
  isSupplierStock?: boolean;
  purchasing?: {
    supplierSettings?: {
      supplierId: string;
      supplierName: string;
      supplierSlug: string;
    };
    locationSettings?: {
      supplierLocationId: string;
      supplierLocationSlug: string;
    };
    purchasePrice?: number;
    stockQuantity?: number;
    supplierCode?: string;
    entryPriceAAKG?: number;
  };
}

// these are order details:
// issuedBy - person who created the work order - this information is sourced from Ritam
export interface IStockOutInfo {
  user: string;
  userId: string;
  issueDate: Date;
  numOfPackages?: number;
  issuedBy: {
    firstName: string;
    lastName: string;
  };
}

export interface DBWorkOrdersCreate {
  customerOrderId?: string;
  erpBranchId: string;
  erpOrderId?: string | null;
  lineItems: IWorkOrderLineItem[];
  webOrderId?: string | null;
  orderStatus: OrderStatusProps;
  externalStatus?: WorkOrdersStatus;
  orderNumber?: string | null;
  updateCustomerOrderStatus?: boolean;
  appointments?: CreateServiceReservationReq[];
  defaultWorkOrderId?: string;
  createRecordInRitam?: boolean;
  stockOutData?: IStockOutInfo;
  serviceErpBranchId?: string | null;
  isPending?: boolean;
  skipReservation?: boolean;
  ritamImport?: boolean;
  erpDocNumber?: string;
  dropShipping?: boolean;
  hotelCode?: string | null;
  generateTransferOrder?: boolean | undefined;
  branchId?: number | null | undefined;
}

export interface DBWorkOrdersUpdate {
  lineItems?: DBOrderItems[];
  orderStatus?: OrderStatusProps;
  externalStatus?: WorkOrdersStatus;
}

export type _WorkOrderType = DBWorkOrders & { appointments: DBAppointments[] };

export type ExtendedCustomerOrder = DBCustomerOrderItem & {
  workOrders: _WorkOrderType[];
  clientSupplierOrders: DBClientSupplierOrderItem[];
  payment: DBPaymentTypes | null;
  deliveryAddress: DBDeliveryAddress | null;
  billingAddress: DBBillingAddress | null;
  delivery: DBDeliveryTypes | null;
};

export interface UpdatedItemAfterInboundResponse {
  updatedWorkOrder: DBWorkOrders | null;
  createdWorkOrder?: DBWorkOrders[];
  ritamResponse: string;
  warningMessage?: string;
  transferOrders?: DBTransferOrderItem[];
  ritamResponseForTO?: string;
}

export type UpdateAfterInboundResponse =
  (UpdatedItemAfterInboundResponse | null)[];

export interface IUpdateAfterInboundResponse {
  customerOrderReference: string;
  response: UpdateAfterInboundResponse;
  message?: string;
  errors?: string[];
}

export const deliveryTypeToSubStatusMap: Record<string, string> = {
  [DeliveryType.WMS_COLLECTION]: InProcessSubStatuses.Ready_for_collection,
  [DeliveryType.WMS_SHIPPING]: InProcessSubStatuses.Ready_for_shipping,
  [DeliveryType.WMS_DISPATCH]: InProcessSubStatuses.Ready_for_shipping,
};

export interface IRemoveReservationQuery {
  status: DBCustomerOrderStatusEnum;
  batchSize: number;
  year: string;
}

export interface DBWorkOrdersApi {
  create: {
    requestBody: DBWorkOrdersCreate;
    requestParams: never;
    request: DBWorkOrdersCreate;
    response: {
      data: DBWorkOrders[];
      ritamResponse: string;
      transferOrders?: {
        ritamResponse: string;
        transferOrder: DBTransferOrderItem[];
      }[];
    };
  };
  updateOne: {
    requestBody: DBWorkOrdersUpdate;
    requestParams: never;
    request: never;
    response: DBWorkOrders;
  };
  getOne: {
    requestBody: never;
    requestParams: { customerOrderId: string };
    request: { customerOrderId: string };
    response: DBWorkOrders;
  };
  preparePrintData: {
    requestBody: never;
    requestParams: { id: string };
    request: { id: string };
    response: string;
  };
  deliveryNotes: {
    requestBody: never;
    requestParams: { customerOrderId: string };
    request: { customerOrderId: string };
    response: DBOrderDocument[];
  };
  cancelOne: {
    requestBody: { workOrder: DBWorkOrders };
    requestParams: never;
    request: { workOrder: DBWorkOrders };
    response: DBWorkOrders;
  };
  updateAfterInvoiceMany: {
    requestBody: {
      body: {
        items: InboundInvoiceItem[];
        branchId: number | undefined;
        shouldCreateTransferOrder: boolean;
        createdVia: 'purchaseOrder' | 'stockIn' | '';
      };
    };
    requestParams: {
      body: { items: InboundInvoiceItem[]; branchId: number | undefined };
    };
    request: {
      body: { items: InboundInvoiceItem[]; branchId: number | undefined };
    };
    response: (IUpdateAfterInboundResponse | null)[];
  };
  getByEan: {
    requestBody: never;
    requestParams: { ean: string; branchId: number };
    request: { ean: string; branchId: number };
    response: {
      data: (DBWorkOrders & { customerOrder: DBCustomerOrderItemResponse })[];
      count: number;
    };
  };
}
